import type { MetaBrandedContentAdPermission } from "../interfaces/MetaTypes";
import type { InstagramPartnerPermissionStatusWire } from "../interfaces/adsManagerGuidedEdit";
import {
  findPartnershipInstagramBrandedContentPermission,
  isApprovedBrandedContentPermissionStatus,
  metaBrandedContentPermissionCreatorFbPageId,
  metaBrandedContentPermissionCreatorIgId,
  normalizeMetaGraphId,
} from "./metaPartnershipAdCreative";
import { toInstagramPartnerPermissionStatusWire } from "./instagramPartnershipCreative";
import {
  buildMetaPartnershipAdsHubUrl,
  META_BUSINESS_SUITE_PARTNERSHIP_ADS_HUB_URL,
} from "./metaAdsManagerLinks";

/**
 * Meta Partnership Ads Hub (Business Suite) — manage partnership requests on desktop.
 * @see https://www.facebook.com/business/help/1195065088493836
 */
export const META_BUSINESS_SUITE_PARTNERSHIPS_URL =
  META_BUSINESS_SUITE_PARTNERSHIP_ADS_HUB_URL;

/** Partnership Ads Hub deep link when the guided flow has ad account (and optional BM) context. */
export function resolveMetaPartnershipAdsHubUrl(adAccountId?: string): string;
export function resolveMetaPartnershipAdsHubUrl(input?: {
  adAccountId?: string;
  businessId?: string;
}): string;
export function resolveMetaPartnershipAdsHubUrl(
  input?: string | { adAccountId?: string; businessId?: string }
): string {
  const normalized =
    typeof input === "string" ? { adAccountId: input } : input;
  return buildMetaPartnershipAdsHubUrl({
    accountId: normalized?.adAccountId,
    businessId: normalized?.businessId,
  });
}

/** Brand-facing Facebook partnership request page (desktop-friendly). */
export const META_PARTNERSHIP_ADS_REQUEST_BASE_URL =
  "https://www.facebook.com/partnership_ads_request";

export type PartnershipPartnerApprovalLinkChannel = "instagram" | "facebook";

export type PartnershipPartnerApprovalLink = {
  channel: PartnershipPartnerApprovalLinkChannel;
  url: string;
  copyLabel: string;
};

export type PartnershipPartnerFacebookPageOption = {
  id: string;
  name: string;
  picture: string;
  username?: string;
  /** Ad-account / business page catalog — selectable partner; does not imply Meta partnership approval. */
  is_ad_account_page?: boolean;
  is_eligible_for_branded_content: boolean | null;
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_status_label: string;
  partnership_has_permission_row: boolean;
  partnership_select_badge: PartnershipSelectBadgeKind;
  partnership_select_badge_label: string;
  partnership_sort_rank: number;
  partnership_usable: boolean;
  partnership_unusable_reason?: string;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
  linked_instagram_user_id?: string;
  linked_instagram_username?: string;
};

export type PartnershipPartnerInstagramAccountOption = {
  id: string;
  name: string;
  username: string;
  followers_count: number;
  profile_picture_url: string;
  /** Ad-account IG catalog — selectable partner; does not imply Meta partnership approval. */
  is_ad_account_page?: boolean;
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_status_label: string;
  partnership_has_permission_row: boolean;
  partnership_select_badge: PartnershipSelectBadgeKind;
  partnership_select_badge_label: string;
  partnership_sort_rank: number;
  partnership_usable: boolean;
  partnership_unusable_reason?: string;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
};

/**
 * GET `/ads/partnership_partner_options` — partnership-aware identity + partner catalog.
 *
 * Use this endpoint (not `GET /ads/pages` or `GET /ads/instagram_accounts`) for guided
 * partnership ads: advertiser identity pickers and partnership partner pickers.
 *
 * - `advertiser_*`: brand-owned rows (`is_ad_account_page !== false`) — Pages/IG the org
 *   can run ads as (`/me/accounts`, `promote_pages`, ad-account `instagram_accounts`).
 * - `partner_*`: superset for the partner picker. External creators (`is_ad_account_page:
 *   false`) appear here only when permission rows are merged into the catalog response.
 *
 * Live permission status for one selected partner may come from
 * `GET /ads/partnership_partner_permissions/lookup` when `partnership_permissions_loaded`
 * is false in catalog.
 */
export type ListPartnershipPartnerOptionsResult = {
  /** Brand-owned Facebook Pages for the advertiser identity picker. */
  advertiser_facebook_pages: PartnershipPartnerFacebookPageOption[];
  /** Facebook Pages for the partnership partner picker (includes external creators when loaded). */
  partner_facebook_pages: PartnershipPartnerFacebookPageOption[];
  /** Instagram accounts for the advertiser identity picker. */
  advertiser_instagram_accounts: PartnershipPartnerInstagramAccountOption[];
  /** Instagram accounts for the partnership partner picker. */
  partner_instagram_accounts: PartnershipPartnerInstagramAccountOption[];
  /**
   * @deprecated Same rows as `partner_facebook_pages`. Prefer explicit `partner_*` / `advertiser_*` arrays.
   */
  facebook_pages: PartnershipPartnerFacebookPageOption[];
  /**
   * @deprecated Same rows as `partner_instagram_accounts`.
   */
  instagram_accounts: PartnershipPartnerInstagramAccountOption[];
  advertiser_identity: {
    page_id?: string;
    instagram_user_id?: string;
    /** Meta Business Manager id for Partnership Ads Hub deep links. */
    business_id?: string;
    validation_messages: string[];
    /** True when IG branded-content + Facebook ALP permission rows were merged into this catalog. */
    partnership_permissions_loaded: boolean;
    partnership_permissions_count: number;
  };
};

/** Row is in the brand ad-account catalog, not a permission-only external creator. */
export function partnershipPartnerOptionIsAdvertiserCatalogMember(args: {
  is_ad_account_page?: boolean;
}): boolean {
  return args.is_ad_account_page !== false;
}

/** Split unified catalog rows into advertiser vs partner picker slices (backend-owned contract). */
export function splitPartnershipPartnerOptionsCatalogSlices<
  TPage extends { is_ad_account_page?: boolean },
  TIg extends { is_ad_account_page?: boolean },
>(args: {
  facebook_pages: readonly TPage[];
  instagram_accounts: readonly TIg[];
}): {
  advertiser_facebook_pages: TPage[];
  partner_facebook_pages: TPage[];
  advertiser_instagram_accounts: TIg[];
  partner_instagram_accounts: TIg[];
} {
  const partner_facebook_pages = [...args.facebook_pages];
  const partner_instagram_accounts = [...args.instagram_accounts];
  return {
    advertiser_facebook_pages: partner_facebook_pages.filter((page) =>
      partnershipPartnerOptionIsAdvertiserCatalogMember(page)
    ),
    partner_facebook_pages,
    advertiser_instagram_accounts: partner_instagram_accounts.filter((account) =>
      partnershipPartnerOptionIsAdvertiserCatalogMember(account)
    ),
    partner_instagram_accounts,
  };
}

export function assembleListPartnershipPartnerOptionsResult(args: {
  facebook_pages: PartnershipPartnerFacebookPageOption[];
  instagram_accounts: PartnershipPartnerInstagramAccountOption[];
  advertiser_identity: ListPartnershipPartnerOptionsResult["advertiser_identity"];
}): ListPartnershipPartnerOptionsResult {
  const slices = splitPartnershipPartnerOptionsCatalogSlices(args);
  return {
    ...slices,
    facebook_pages: slices.partner_facebook_pages,
    instagram_accounts: slices.partner_instagram_accounts,
    advertiser_identity: args.advertiser_identity,
  };
}

export function partnershipPartnerSortRank(args: {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_has_permission_row: boolean;
  permissions_loaded: boolean;
  is_eligible_for_branded_content?: boolean | null;
  partnership_usable: boolean;
}): number {
  if (args.is_eligible_for_branded_content === false) {
    return 100;
  }
  if (isApprovedBrandedContentPermissionStatus(args.partnership_permission_status)) {
    return args.partnership_usable ? 0 : 1;
  }
  if (args.partnership_permission_status === "PENDING") {
    return 2;
  }
  if (args.partnership_permission_status === "REVOKED") {
    return 3;
  }
  if (args.permissions_loaded && !args.partnership_has_permission_row) {
    return 4;
  }
  return 5;
}

export function partnershipStatusLabel(args: {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_has_permission_row: boolean;
  permissions_loaded: boolean;
}): string {
  if (args.partnership_has_permission_row) {
    switch (args.partnership_permission_status) {
      case "APPROVED":
        return "Partnership approved";
      case "PENDING":
        return "Partnership pending";
      case "REVOKED":
        return "Partnership revoked";
      default:
        return "Partnership status unknown";
    }
  }
  if (!args.permissions_loaded) {
    return "Partnership not verified";
  }
  return "No partnership yet";
}

/** Select-option badge in partner Facebook / Instagram pickers. */
export type PartnershipSelectBadgeKind =
  | "approved"
  | "eligible"
  | "pending"
  | "not_eligible";

export const PARTNERSHIP_SELECT_BADGE_LABEL: Record<
  PartnershipSelectBadgeKind,
  string
> = {
  approved: "Approved",
  eligible: "Eligible",
  pending: "Request sent",
  not_eligible: "Not eligible",
};

export function resolvePartnershipSelectBadge(args: {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_status_label: string;
  partnership_has_permission_row: boolean;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
  is_eligible_for_branded_content?: boolean | null;
  is_ad_account_page?: boolean;
}): {
  partnership_select_badge: PartnershipSelectBadgeKind;
  partnership_select_badge_label: string;
} {
  const kind = resolvePartnershipSelectBadgeKind(args);
  return {
    partnership_select_badge: kind,
    partnership_select_badge_label: PARTNERSHIP_SELECT_BADGE_LABEL[kind],
  };
}

function resolvePartnershipSelectBadgeKind(args: {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_status_label: string;
  partnership_has_permission_row: boolean;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
  is_eligible_for_branded_content?: boolean | null;
  is_ad_account_page?: boolean;
}): PartnershipSelectBadgeKind {
  if (args.is_eligible_for_branded_content === false) {
    return "not_eligible";
  }

  if (
    args.partnership_permission_status === "REVOKED" ||
    args.partnership_status_label.trim() === "Partnership revoked"
  ) {
    return "not_eligible";
  }

  if (
    isApprovedBrandedContentPermissionStatus(
      args.partnership_permission_status
    )
  ) {
    return "approved";
  }

  if (
    isPartnershipPermissionRequestOutstanding(args) ||
    args.partnership_status_label.trim() === "Partnership pending"
  ) {
    return "pending";
  }

  // Default: partner is in catalog and the brand can send a request.
  return "eligible";
}

export type RequestPartnershipPartnerPermissionResult = {
  permission_id: string;
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_status_label: string;
  partnership_has_permission_row: boolean;
  partnership_permission_url?: string;
};

/**
 * GET `/ads/partnership_partner_permissions/lookup` — live permission status for one
 * selected partner. Does not list pages or Instagram accounts; use
 * `partnership_partner_options` for pickers. Meta: targeted page GETs, IG
 * `branded_content_ad_permissions` scan, Facebook ALP for the partner page.
 */
export type LookupPartnershipPartnerPermissionResult = PartnershipPermissionMeta & {
  partnership_select_badge: PartnershipSelectBadgeKind;
  partnership_select_badge_label: string;
  partnership_permissions_loaded: true;
};

/** Default permission meta for catalog rows before lazy lookup on partner select. */
export function catalogUnknownPartnershipPermissionMeta(): PartnershipPermissionMeta {
  return {
    partnership_permission_status: "UNKNOWN",
    partnership_status_label: partnershipStatusLabel({
      partnership_permission_status: "UNKNOWN",
      partnership_has_permission_row: false,
      permissions_loaded: false,
    }),
    partnership_has_permission_row: false,
  };
}

export function buildLookupPartnershipPartnerPermissionResult(args: {
  permissionMeta: PartnershipPermissionMeta;
  isEligibleForBrandedContent?: boolean | null;
  isAdAccountPage?: boolean;
}): LookupPartnershipPartnerPermissionResult {
  const badge = resolvePartnershipSelectBadge({
    partnership_permission_status: args.permissionMeta.partnership_permission_status,
    partnership_status_label: args.permissionMeta.partnership_status_label,
    partnership_has_permission_row: args.permissionMeta.partnership_has_permission_row,
    partnership_permission_url: args.permissionMeta.partnership_permission_url,
    partnership_permission_id: args.permissionMeta.partnership_permission_id,
    is_eligible_for_branded_content: args.isEligibleForBrandedContent ?? null,
    is_ad_account_page: args.isAdAccountPage,
  });
  return {
    ...args.permissionMeta,
    partnership_select_badge: badge.partnership_select_badge,
    partnership_select_badge_label: badge.partnership_select_badge_label,
    partnership_permissions_loaded: true,
  };
}

/** Meta ALP `partner_page_ids` query value (JSON array of numeric page ids). */
export function facebookAlpPartnerPageIdsQueryValue(
  partnerFacebookPageId: string
): string {
  return JSON.stringify([
    metaFacebookPageIdForAccountLevelPermissionApi(partnerFacebookPageId),
  ]);
}

/** True when two Facebook page ids refer to the same Page (including numeric normalization). */
export function isSameMetaPartnershipFacebookPage(
  left?: string | null,
  right?: string | null
): boolean {
  const a = left?.trim();
  const b = right?.trim();
  if (!a || !b) {
    return false;
  }
  return a === b || normalizeMetaGraphId(a) === normalizeMetaGraphId(b);
}

export function partnershipPermissionUrlFields(permissionMeta: {
  partnership_permission_url?: string;
}): { partnership_permission_url?: string } {
  return permissionMeta.partnership_permission_url
    ? { partnership_permission_url: permissionMeta.partnership_permission_url }
    : {};
}

export function partnershipPermissionIdFields(permissionMeta: {
  partnership_permission_id?: string;
}): { partnership_permission_id?: string } {
  return permissionMeta.partnership_permission_id
    ? { partnership_permission_id: permissionMeta.partnership_permission_id }
    : {};
}

export function classifyPartnershipPermissionUrl(
  url: string
): PartnershipPartnerApprovalLinkChannel | undefined {
  const trimmed = url.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    const host = new URL(trimmed).hostname.toLowerCase();
    if (host.includes("instagram.com")) {
      return "instagram";
    }
    if (host.includes("facebook.com") || host.includes("fb.com")) {
      return "facebook";
    }
  } catch {
    if (trimmed.includes("instagram.com")) {
      return "instagram";
    }
    if (trimmed.includes("facebook.com") || trimmed.includes("fb.com")) {
      return "facebook";
    }
  }
  return undefined;
}

export function buildFacebookPartnershipAdsRequestUrl(
  permissionId: string
): string | undefined {
  const id = permissionId.trim();
  if (!id) {
    return undefined;
  }
  return `${META_PARTNERSHIP_ADS_REQUEST_BASE_URL}?request_id=${encodeURIComponent(id)}`;
}

const PARTNERSHIP_PARTNER_APPROVAL_COPY_LABELS: Record<
  PartnershipPartnerApprovalLinkChannel,
  string
> = {
  instagram: "Copy Instagram approval link",
  facebook: "Copy Facebook approval link",
};

/** Partner-facing approval links to share (Instagram deep link + Facebook request page). */
export function resolvePartnershipPartnerApprovalLinks(args: {
  partnership_permission_url?: string;
  partnership_permission_id?: string;
}): PartnershipPartnerApprovalLink[] {
  const links: PartnershipPartnerApprovalLink[] = [];
  const seenUrls = new Set<string>();

  const add = (
    channel: PartnershipPartnerApprovalLinkChannel,
    url: string
  ) => {
    const normalized = url.trim();
    if (!normalized || seenUrls.has(normalized)) {
      return;
    }
    seenUrls.add(normalized);
    links.push({
      channel,
      url: normalized,
      copyLabel: PARTNERSHIP_PARTNER_APPROVAL_COPY_LABELS[channel],
    });
  };

  const permissionUrl = normalizePartnershipPartnerApprovalUrl(
    args.partnership_permission_url
  );
  if (permissionUrl) {
    const channel = classifyPartnershipPermissionUrl(permissionUrl);
    if (channel) {
      add(channel, permissionUrl);
    }
  }

  const permissionId = args.partnership_permission_id?.trim();
  if (permissionId) {
    const facebookUrl = buildFacebookPartnershipAdsRequestUrl(permissionId);
    if (facebookUrl && !links.some((link) => link.channel === "facebook")) {
      add("facebook", facebookUrl);
    }
  }

  const channelOrder: Record<PartnershipPartnerApprovalLinkChannel, number> = {
    instagram: 0,
    facebook: 1,
  };
  return [...links].sort(
    (a, b) => channelOrder[a.channel] - channelOrder[b.channel]
  );
}

function normalizePartnershipPartnerApprovalUrl(
  url: string | undefined
): string | undefined {
  const trimmed = url?.trim();
  if (!trimmed) {
    return undefined;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`;
  }
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

export const PARTNERSHIP_PARTNER_FB_IG_MISMATCH_MESSAGE =
  "The partner Instagram account must match the Instagram linked to the selected Facebook Page in Meta. Clear the Facebook page to use Instagram only, or pick the linked account.";

/** True when both partner FB and IG are set but IG is not the page's linked Instagram (Meta publishes with the page sponsor). */
export function partnershipPartnerFacebookInstagramMismatch(args: {
  partnerFacebookPageId?: string;
  partnerInstagramUserId?: string;
  linkedInstagramUserId?: string;
}): boolean {
  const partnerFacebookPageId = args.partnerFacebookPageId?.trim();
  const partnerInstagramUserId = args.partnerInstagramUserId?.trim();
  if (!partnerFacebookPageId || !partnerInstagramUserId) {
    return false;
  }
  const linkedInstagramUserId = args.linkedInstagramUserId?.trim();
  if (!linkedInstagramUserId) {
    return false;
  }
  return partnerInstagramUserId !== linkedInstagramUserId;
}

export function partnershipPartnerFacebookInstagramMismatchMessage(args: {
  partnerFacebookPageId?: string;
  partnerInstagramUserId?: string;
  linkedInstagramUserId?: string;
}): string | null {
  return partnershipPartnerFacebookInstagramMismatch(args)
    ? PARTNERSHIP_PARTNER_FB_IG_MISMATCH_MESSAGE
    : null;
}

export const META_FB_ACCOUNT_LEVEL_PERMISSIONS_API_BASE =
  "https://api.facebook.com/partnership-ads/fb-account-level-permissions";

export const META_FB_ACCOUNT_LEVEL_PERMISSIONS_API_VERSION = "1.0.0";

/** Meta FB account-level permission POST bodies require numeric page ids, not strings. */
export function metaFacebookPageIdForAccountLevelPermissionApi(
  pageId: string
): number {
  const normalized = normalizeMetaGraphId(pageId) ?? pageId.trim();
  if (!/^\d+$/.test(normalized)) {
    throw new Error(`Invalid Facebook Page id "${pageId}".`);
  }
  const asNumber = Number(normalized);
  if (!Number.isFinite(asNumber) || !Number.isInteger(asNumber)) {
    throw new Error(`Invalid Facebook Page id "${pageId}".`);
  }
  return asNumber;
}

/** Meta Facebook account-level permission numeric status → guided wire status. */
export function mapFacebookAccountLevelPermissionStatusToWire(
  alpStatus: number | undefined
): InstagramPartnerPermissionStatusWire {
  if (alpStatus === 2 || alpStatus === 7) {
    return "APPROVED";
  }
  if (alpStatus === 1) {
    return "PENDING";
  }
  if (alpStatus === 3 || alpStatus === 4) {
    return "REVOKED";
  }
  return "UNKNOWN";
}

export type FacebookAccountLevelPartnershipPermissionRow = {
  id: string;
  partner_page_id: string;
  status: number;
  permission_direction?: string;
};

export type PartnershipPermissionMeta = {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_status_label: string;
  partnership_has_permission_row: boolean;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
};

const PARTNERSHIP_PERMISSION_STATUS_RANK: Record<
  InstagramPartnerPermissionStatusWire,
  number
> = {
  APPROVED: 0,
  PENDING: 1,
  REVOKED: 2,
  UNKNOWN: 3,
};

function facebookAlpStatusPrecedence(status: number): number {
  if (status === 2 || status === 7) {
    return 0;
  }
  if (status === 1) {
    return 1;
  }
  if (status === 3 || status === 4) {
    return 2;
  }
  return 3;
}

export function buildFacebookAccountLevelPermissionsByPartnerPageId(
  rows: readonly FacebookAccountLevelPartnershipPermissionRow[]
): Map<string, FacebookAccountLevelPartnershipPermissionRow> {
  const map = new Map<string, FacebookAccountLevelPartnershipPermissionRow>();
  for (const row of rows) {
    const partnerPageId =
      normalizeMetaGraphId(row.partner_page_id) ?? row.partner_page_id.trim();
    if (!partnerPageId) {
      continue;
    }
    const existing = map.get(partnerPageId);
    if (
      !existing ||
      facebookAlpStatusPrecedence(row.status) <
        facebookAlpStatusPrecedence(existing.status)
    ) {
      map.set(partnerPageId, row);
    }
  }
  return map;
}

export function resolveFacebookAccountLevelPermissionMeta(
  partnerFacebookPageId: string | undefined,
  permissionsByPartnerPageId: ReadonlyMap<
    string,
    FacebookAccountLevelPartnershipPermissionRow
  >,
  permissionsLoaded: boolean
): PartnershipPermissionMeta {
  const pageId =
    normalizeMetaGraphId(partnerFacebookPageId) ??
    partnerFacebookPageId?.trim();
  if (!pageId) {
    return {
      partnership_permission_status: "UNKNOWN",
      partnership_status_label: partnershipStatusLabel({
        partnership_permission_status: "UNKNOWN",
        partnership_has_permission_row: false,
        permissions_loaded: permissionsLoaded,
      }),
      partnership_has_permission_row: false,
    };
  }

  const row = permissionsByPartnerPageId.get(pageId);
  if (!row) {
    return {
      partnership_permission_status: "UNKNOWN",
      partnership_status_label: partnershipStatusLabel({
        partnership_permission_status: "UNKNOWN",
        partnership_has_permission_row: false,
        permissions_loaded: permissionsLoaded,
      }),
      partnership_has_permission_row: false,
    };
  }

  const partnership_permission_status =
    mapFacebookAccountLevelPermissionStatusToWire(row.status);
  const permissionId = row.id.trim();
  const partnership_permission_url =
    buildFacebookPartnershipAdsRequestUrl(permissionId);

  return {
    partnership_permission_status,
    partnership_status_label: partnershipStatusLabel({
      partnership_permission_status,
      partnership_has_permission_row: true,
      permissions_loaded: permissionsLoaded,
    }),
    partnership_has_permission_row: true,
    partnership_permission_id: permissionId || undefined,
    ...(partnership_permission_url
      ? { partnership_permission_url }
      : {}),
  };
}

/** Prefer the more actionable status when IG and Facebook account-level rows both exist. */
export function mergePartnershipPermissionMeta(
  instagram: PartnershipPermissionMeta,
  facebook: PartnershipPermissionMeta | undefined
): PartnershipPermissionMeta {
  if (!facebook?.partnership_has_permission_row) {
    return instagram;
  }
  if (!instagram.partnership_has_permission_row) {
    return facebook;
  }

  const igRank =
    PARTNERSHIP_PERMISSION_STATUS_RANK[instagram.partnership_permission_status];
  const fbRank =
    PARTNERSHIP_PERMISSION_STATUS_RANK[facebook.partnership_permission_status];
  const primary = igRank <= fbRank ? instagram : facebook;
  const secondary = primary === instagram ? facebook : instagram;

  return {
    ...primary,
    partnership_permission_url:
      primary.partnership_permission_url ?? secondary.partnership_permission_url,
    partnership_permission_id:
      primary.partnership_permission_id ?? secondary.partnership_permission_id,
  };
}

export function mergePartnershipPermissionRows(
  existing: readonly MetaBrandedContentAdPermission[],
  incoming: readonly MetaBrandedContentAdPermission[]
): MetaBrandedContentAdPermission[] {
  const byKey = new Map<string, MetaBrandedContentAdPermission>();
  const keyOf = (row: MetaBrandedContentAdPermission): string => {
    const id = row.id?.trim();
    if (id) {
      return `id:${id}`;
    }
    return [
      metaBrandedContentPermissionCreatorIgId(row) ?? "",
      metaBrandedContentPermissionCreatorFbPageId(row) ?? "",
      row.permission_status ?? "",
    ].join(":");
  };
  for (const row of [...existing, ...incoming]) {
    byKey.set(keyOf(row), row);
  }
  return [...byKey.values()];
}

export function mergePartnershipPermissionWithFacebookAlp(
  instagramMeta: PartnershipPermissionMeta,
  partnerFacebookPageId: string | undefined,
  facebookAlpByPartnerPageId: ReadonlyMap<
    string,
    FacebookAccountLevelPartnershipPermissionRow
  >,
  facebookAlpPermissionsLoaded: boolean
): PartnershipPermissionMeta {
  if (!partnerFacebookPageId?.trim()) {
    return instagramMeta;
  }
  return mergePartnershipPermissionMeta(
    instagramMeta,
    resolveFacebookAccountLevelPermissionMeta(
      partnerFacebookPageId,
      facebookAlpByPartnerPageId,
      facebookAlpPermissionsLoaded
    )
  );
}

/** Inputs shared when turning fetched Meta rows into partnership picker options. */
export type PartnershipPartnerOptionsCatalogContext = {
  advertiserPageId?: string;
  advertiserInstagramUserId?: string;
  permissions: MetaBrandedContentAdPermission[];
  permissionsCount: number;
  partnershipPermissionsLoaded: boolean;
  partnershipPermissionResolveOptions: {
    permissions_loaded: boolean;
    creator_ig_to_page_id: Map<string, string>;
  };
  facebookAlpByPartnerPageId: ReadonlyMap<
    string,
    FacebookAccountLevelPartnershipPermissionRow
  >;
  facebookAlpPermissionsLoaded: boolean;
};

export function resolveCatalogPartnerPermissionMeta(
  ctx: PartnershipPartnerOptionsCatalogContext,
  partner: {
    partnerPageId?: string;
    partnerInstagramUserId?: string;
    partnerInstagramUsername?: string;
  }
): PartnershipPermissionMeta {
  const instagramMeta = resolvePartnershipPermissionForPartner(
    ctx.permissions,
    {
      facebookPageId: partner.partnerPageId,
      instagramUserId: partner.partnerInstagramUserId,
      instagramUsername: partner.partnerInstagramUsername,
    },
    ctx.partnershipPermissionResolveOptions
  );
  return mergePartnershipPermissionWithFacebookAlp(
    instagramMeta,
    partner.partnerPageId,
    ctx.facebookAlpByPartnerPageId,
    ctx.facebookAlpPermissionsLoaded
  );
}

export function catalogPartnerOptionPermissionFields(
  ctx: PartnershipPartnerOptionsCatalogContext,
  args: {
    permissionMeta: PartnershipPermissionMeta;
    isEligibleForBrandedContent: boolean | null;
    isAdAccountPage: boolean;
    partnershipHasPermissionRow?: boolean;
    selectableWithoutApprovedPartnership?: boolean;
    partnerPageId?: string;
    partnerInstagramUserId?: string;
  }
): ReturnType<typeof partnershipPartnerOptionPermissionFields> {
  const partnershipHasPermissionRow =
    args.partnershipHasPermissionRow ??
    args.permissionMeta.partnership_has_permission_row;
  const usability = evaluatePartnershipPartnerUsability({
    advertiserPageId: ctx.advertiserPageId,
    advertiserInstagramUserId: ctx.advertiserInstagramUserId,
    partnerPageId: args.partnerPageId,
    partnerInstagramUserId: args.partnerInstagramUserId,
    isEligibleForBrandedContent: args.isEligibleForBrandedContent,
    permissionStatus: args.permissionMeta.partnership_permission_status,
    brandedContentPermissionsCount: ctx.permissionsCount,
    partnershipHasPermissionRow,
    selectable_without_approved_partnership:
      args.selectableWithoutApprovedPartnership,
  });
  return partnershipPartnerOptionPermissionFields({
    permissionMeta: args.permissionMeta,
    usability,
    permissionsLoaded: ctx.partnershipPermissionsLoaded,
    isEligibleForBrandedContent: args.isEligibleForBrandedContent,
    isAdAccountPage: args.isAdAccountPage,
    partnershipHasPermissionRow: args.partnershipHasPermissionRow,
  });
}

export function partnershipPartnerOptionPermissionFields(args: {
  permissionMeta: PartnershipPermissionMeta;
  usability: {
    partnership_usable: boolean;
    partnership_unusable_reason?: string;
  };
  permissionsLoaded: boolean;
  isEligibleForBrandedContent: boolean | null;
  isAdAccountPage: boolean;
  partnershipHasPermissionRow?: boolean;
}): Pick<
  PartnershipPartnerFacebookPageOption,
  | "partnership_permission_status"
  | "partnership_status_label"
  | "partnership_has_permission_row"
  | "partnership_select_badge"
  | "partnership_select_badge_label"
  | "partnership_sort_rank"
  | "partnership_usable"
> & {
  partnership_unusable_reason?: string;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
} {
  const partnership_has_permission_row =
    args.partnershipHasPermissionRow ??
    args.permissionMeta.partnership_has_permission_row;

  return {
    partnership_permission_status: args.permissionMeta.partnership_permission_status,
    partnership_status_label: args.permissionMeta.partnership_status_label,
    partnership_has_permission_row,
    ...resolvePartnershipSelectBadge({
      partnership_permission_status:
        args.permissionMeta.partnership_permission_status,
      partnership_status_label: args.permissionMeta.partnership_status_label,
      partnership_has_permission_row,
      partnership_permission_url: args.permissionMeta.partnership_permission_url,
      partnership_permission_id: args.permissionMeta.partnership_permission_id,
      is_eligible_for_branded_content: args.isEligibleForBrandedContent,
      is_ad_account_page: args.isAdAccountPage,
    }),
    partnership_sort_rank: partnershipPartnerSortRank({
      partnership_permission_status:
        args.permissionMeta.partnership_permission_status,
      partnership_has_permission_row,
      permissions_loaded: args.permissionsLoaded,
      is_eligible_for_branded_content: args.isEligibleForBrandedContent,
      partnership_usable: args.usability.partnership_usable,
    }),
    partnership_usable: args.usability.partnership_usable,
    ...(args.usability.partnership_unusable_reason
      ? {
          partnership_unusable_reason: args.usability.partnership_unusable_reason,
        }
      : {}),
    ...partnershipPermissionUrlFields(args.permissionMeta),
    ...partnershipPermissionIdFields(args.permissionMeta),
  };
}

/** Partnership requests use Instagram account-level API when creator IG is known; otherwise Facebook page-level API. */
export function resolvePartnershipPartnerRequestIdentity(args: {
  partnerInstagramUserId?: string;
  partnerInstagramUsername?: string;
  partnerFacebookPageId?: string;
  linkedInstagramUserId?: string;
  linkedInstagramUsername?: string;
}): {
  partnerInstagramUserId?: string;
  partnerInstagramUsername?: string;
  partnerFacebookPageId?: string;
  requestable: boolean;
} {
  const partnerInstagramUserId =
    args.partnerInstagramUserId?.trim() ||
    args.linkedInstagramUserId?.trim() ||
    undefined;
  const partnerInstagramUsername = partnerInstagramUserId
    ? undefined
    : args.partnerInstagramUsername?.trim().replace(/^@/, "") ||
      args.linkedInstagramUsername?.trim().replace(/^@/, "") ||
      undefined;
  const partnerFacebookPageId = args.partnerFacebookPageId?.trim() || undefined;

  return {
    partnerInstagramUserId,
    partnerInstagramUsername,
    partnerFacebookPageId,
    requestable: Boolean(
      partnerInstagramUserId ||
        partnerInstagramUsername ||
        partnerFacebookPageId
    ),
  };
}

/** Partner FB page is in the brand's /me/accounts catalog (same source as is_ad_account_page). */
export function partnershipManagedCatalogIncludesFacebookPage(args: {
  partnerFacebookPageId?: string;
  managedPages: ReadonlyArray<{ id: string }>;
}): boolean {
  const partnerId = normalizeMetaGraphId(args.partnerFacebookPageId);
  if (!partnerId) {
    return false;
  }
  return args.managedPages.some(
    (page) => normalizeMetaGraphId(page.id) === partnerId
  );
}

/** Page is in the brand ad-account catalog (not permission-only external creator page). */
export function partnershipPartnerFacebookPageIsAdvertiserAccessible(args: {
  page?: { id?: string; is_ad_account_page?: boolean };
  advertiserCatalogPageIds?: ReadonlySet<string>;
}): boolean {
  const pageId = args.page?.id?.trim();
  if (!pageId) {
    return false;
  }
  if (args.page?.is_ad_account_page === true) {
    return true;
  }
  if (args.page?.is_ad_account_page === false) {
    return false;
  }
  return Boolean(args.advertiserCatalogPageIds?.has(pageId));
}

/** Meta account-level permission requests need a creator Instagram id, @username, or partner FB page id. */
export function hasRequestablePartnerInstagramForPermissionRequest(args: {
  partnerInstagramUserId?: string;
  partnerInstagramUsername?: string;
  partnerFacebookPageId?: string;
}): boolean {
  if (
    args.partnerInstagramUserId?.trim() ||
    args.partnerInstagramUsername?.trim()
  ) {
    return true;
  }
  return Boolean(args.partnerFacebookPageId?.trim());
}

/** Passthrough for stored/API permission status (catalog membership does not change status). */
export function resolveEffectivePartnershipPermissionStatus(args: {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
}): InstagramPartnerPermissionStatusWire {
  return args.partnership_permission_status;
}

export const PARTNERSHIP_AD_ACCOUNT_PAGE_STATUS_LABEL =
  "Page in your ad account";

export function partnershipLinkedInstagramFields(args: {
  linkedInstagramUserId?: string;
  linkedInstagramUsername?: string;
}): {
  linked_instagram_user_id?: string;
  linked_instagram_username?: string;
} {
  const linked_instagram_user_id = args.linkedInstagramUserId?.trim();
  const linked_instagram_username =
    args.linkedInstagramUsername?.trim().replace(/^@+/, "") || undefined;
  if (!linked_instagram_user_id && !linked_instagram_username) {
    return {};
  }
  return {
    ...(linked_instagram_user_id ? { linked_instagram_user_id } : {}),
    ...(linked_instagram_username ? { linked_instagram_username } : {}),
  };
}

/** Meta already has a permission row waiting on the partner (not approved or revoked). */
export function isPartnershipPermissionRequestOutstanding(args: {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_has_permission_row?: boolean;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
}): boolean {
  if (isApprovedBrandedContentPermissionStatus(args.partnership_permission_status)) {
    return false;
  }
  if (args.partnership_permission_status === "REVOKED") {
    return false;
  }
  if (args.partnership_permission_status === "PENDING") {
    return true;
  }
  if (args.partnership_has_permission_row === true) {
    return true;
  }
  return false;
}

/** Shareable Instagram/Facebook approval links — only after Meta has a pending request. */
export function shouldShowPartnershipPartnerApprovalLinks(args: {
  requestJustSent?: boolean;
  partnershipRequestOutstanding: boolean;
}): boolean {
  return Boolean(args.requestJustSent || args.partnershipRequestOutstanding);
}

/** True when the brand can send a Meta account-level partnership permission request for this partner. */
export function shouldShowRequestPartnershipButton(args: {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_has_permission_row?: boolean;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
  is_eligible_for_branded_content?: boolean | null;
  /** Partner has IG/username and/or a Facebook Page id Meta can resolve for the request. */
  hasRequestablePartnerInstagram: boolean;
}): boolean {
  if (!args.hasRequestablePartnerInstagram) {
    return false;
  }
  if (args.is_eligible_for_branded_content === false) {
    return false;
  }
  if (isApprovedBrandedContentPermissionStatus(args.partnership_permission_status)) {
    return false;
  }
  if (isPartnershipPermissionRequestOutstanding(args)) {
    return false;
  }
  return true;
}

export function isPartnershipPartnerSelectable(args: {
  is_eligible_for_branded_content?: boolean | null;
}): boolean {
  return args.is_eligible_for_branded_content !== false;
}

/** Picker disable: only hard-block not eligible / revoked (red badge). */
export function isPartnershipPartnerOptionDisabled(args: {
  is_eligible_for_branded_content?: boolean | null;
  partnership_select_badge?: PartnershipSelectBadgeKind;
}): boolean {
  return (
    !isPartnershipPartnerSelectable(args) ||
    args.partnership_select_badge === "not_eligible"
  );
}

export function resolvePartnershipPermissionForPartner(
  permissions: readonly MetaBrandedContentAdPermission[],
  partner: {
    instagramUserId?: string;
    facebookPageId?: string;
    instagramUsername?: string;
  },
  options: {
    permissions_loaded: boolean;
    creator_ig_to_page_id?: ReadonlyMap<string, string>;
  }
): {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_status_label: string;
  partnership_has_permission_row: boolean;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
} {
  const matched = findPartnershipInstagramBrandedContentPermission(
    permissions,
    partner,
    { creator_ig_to_page_id: options.creator_ig_to_page_id }
  );
  if (matched?.permission_status) {
    const partnership_permission_status = toInstagramPartnerPermissionStatusWire(
      matched.permission_status
    );
    return {
      partnership_permission_status,
      partnership_status_label: partnershipStatusLabel({
        partnership_permission_status,
        partnership_has_permission_row: true,
        permissions_loaded: options.permissions_loaded,
      }),
      partnership_has_permission_row: true,
      partnership_permission_url: matched.permission_url?.trim() || undefined,
      partnership_permission_id: matched.id?.trim() || undefined,
    };
  }

  const partnership_permission_status: InstagramPartnerPermissionStatusWire =
    "UNKNOWN";
  return {
    partnership_permission_status,
    partnership_status_label: partnershipStatusLabel({
      partnership_permission_status,
      partnership_has_permission_row: false,
      permissions_loaded: options.permissions_loaded,
    }),
    partnership_has_permission_row: false,
  };
}

export function comparePartnershipPartnerOptions(
  a: { partnership_sort_rank: number; name: string },
  b: { partnership_sort_rank: number; name: string }
): number {
  if (a.partnership_sort_rank !== b.partnership_sort_rank) {
    return a.partnership_sort_rank - b.partnership_sort_rank;
  }
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

export function sortPartnershipPartnerFacebookPages<
  T extends PartnershipPartnerFacebookPageOption,
>(pages: readonly T[]): T[] {
  return [...pages].sort(comparePartnershipPartnerOptions);
}

export function sortPartnershipPartnerInstagramAccounts<
  T extends PartnershipPartnerInstagramAccountOption,
>(accounts: readonly T[]): T[] {
  return [...accounts].sort(comparePartnershipPartnerOptions);
}

export function buildAdvertiserPartnershipValidationMessages(args: {
  advertiserPageId?: string;
  advertiserInstagramUserId?: string;
}): string[] {
  const messages: string[] = [];
  if (!args.advertiserPageId?.trim()) {
    messages.push("Select an advertiser Facebook page before choosing a partner.");
  }
  if (!args.advertiserInstagramUserId?.trim()) {
    messages.push(
      "Select an advertiser Instagram account — partnership ads require a brand Instagram connected to the advertiser page."
    );
  }
  return messages;
}

export function evaluatePartnershipPartnerUsability(args: {
  advertiserPageId?: string;
  advertiserInstagramUserId?: string;
  partnerPageId?: string;
  partnerInstagramUserId?: string;
  isEligibleForBrandedContent: boolean | null;
  permissionStatus: InstagramPartnerPermissionStatusWire;
  brandedContentPermissionsCount: number;
  partnershipHasPermissionRow: boolean;
  /** Meta catalog row (ad-account / page-linked IG): selectable to request partnership before approval. */
  selectable_without_approved_partnership?: boolean;
}): { partnership_usable: boolean; partnership_unusable_reason?: string } {
  const advertiserPageId = args.advertiserPageId?.trim();
  const advertiserInstagramUserId = args.advertiserInstagramUserId?.trim();
  const partnerPageId = args.partnerPageId?.trim();
  const partnerInstagramUserId = args.partnerInstagramUserId?.trim();

  if (!advertiserPageId) {
    return {
      partnership_usable: false,
      partnership_unusable_reason: "Select advertiser Facebook page first",
    };
  }
  if (!advertiserInstagramUserId) {
    return {
      partnership_usable: false,
      partnership_unusable_reason: "Select advertiser Instagram account first",
    };
  }
  if (partnerPageId && partnerPageId === advertiserPageId) {
    return {
      partnership_usable: false,
      partnership_unusable_reason: "Cannot use the advertiser page as partner",
    };
  }
  if (
    partnerInstagramUserId &&
    partnerInstagramUserId === advertiserInstagramUserId
  ) {
    return {
      partnership_usable: false,
      partnership_unusable_reason:
        "Cannot use the advertiser Instagram account as partner",
    };
  }
  if (args.isEligibleForBrandedContent === false) {
    return {
      partnership_usable: false,
      partnership_unusable_reason: "Page not eligible for branded content",
    };
  }
  if (args.permissionStatus === "PENDING") {
    return {
      partnership_usable: true,
      partnership_unusable_reason: "Partnership approval pending in Meta",
    };
  }
  if (args.permissionStatus === "REVOKED") {
    return {
      partnership_usable: false,
      partnership_unusable_reason: "Partnership was revoked in Meta",
    };
  }
  if (isApprovedBrandedContentPermissionStatus(args.permissionStatus)) {
    return { partnership_usable: true };
  }

  // Empty permissions list: Meta may still allow create (same as publish preflight).
  if (args.brandedContentPermissionsCount === 0) {
    return { partnership_usable: true };
  }

  if (args.permissionStatus === "UNKNOWN") {
    if (!args.partnershipHasPermissionRow) {
      return { partnership_usable: true };
    }
    // Meta returned a permission row we do not map (e.g. non-standard status) — still
    // selectable so the brand can re-request or use permission_url from the picker.
    return { partnership_usable: true };
  }

  return { partnership_usable: false };
}
